import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'

import * as state from '../../../../../../../state'
import * as modals from '../../modals'

const SVC_CENTER_ENTITY_ID =
  ents.extra.quiver.resources.data.dimensions.serviceCenters.entities
    .serviceCenter.MODEL_ID

/**
 * Component for managing service centers within a space. Displays a table of
 * service centers. Allows creating, editing, and deleting service centers, with
 * confirmation for deletions.
 */
@ngCore.Component({
  imports: [
    ds.table.Component,
    ds.layout.page.Component,
    ds.overlay.confirmation.Component,
    modals.serviceCenter.Component,
  ],
  selector: 'atn-space-service-centers',
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component {
  /** @see {@link state.dms.schema.Feature} */
  private readonly _schemaFeature = ngCore.inject(state.dms.schema.Feature)

  /** @see {@link state.space.full.serviceCenters.Collection} */
  public readonly serviceCentersEc = ngCore.inject(
    state.space.full.serviceCenters.Collection,
  )

  /** @see {@link state.me.Feature} */
  private readonly _meFeature = ngCore.inject(state.me.Feature)

  /** @see {@link state.space.userAccess.surveyAdmins.Collection} */
  private readonly _surveyAdminsEc = ngCore.inject(
    state.space.userAccess.surveyAdmins.Collection,
  )

  /**
   * Whether the current user can create service centers: platform admins,
   * tenant admins, and survey admins.
   */
  public readonly canManageServiceCentersSg = ngCore.computed(
    (
      canManageUsers = this._meFeature.contexts.selectors.currentUserCanManageUsers.asSignal(),
      currentUserId = this._meFeature.selectors.currentUserId.asSignal(),
      surveyAdminUserIds = new Set(
        this._surveyAdminsEc.plugins.currentListPlugin.feature.selectors.entities
          .asSignal()
          .map(({ userId }) => userId),
      ),
    ) =>
      !!currentUserId &&
      (canManageUsers || surveyAdminUserIds.has(currentUserId)),
  )

  /** Canonical table input for service centers. */
  public readonly tableConfigSg = ngCore.computed(
    (
      title = this.tableNameSg(),
      canManageServiceCenters = this.canManageServiceCentersSg(),
    ) =>
      ({
        collection: this.serviceCentersEc,
        view: {
          creation: canManageServiceCenters,
          showColumnSettings: false,
          title,
        },
      }) satisfies ds.table.ec.Config.Input<state.space.full.serviceCenters.TypeParams>,
  )

  /**
   * Service center marked for deletion, used to display deletion confirmation
   * overlay and related information.
   */
  public readonly deletedServiceCenter = ngCore.computed<
    ents.extra.space.ServiceCenter | undefined
  >(
    (
      [
        deletedEntity,
      ] = this.serviceCentersEc.plugins.editingPlugin.feature.selectors
        .deletedEntities()
        .asSignal(),
    ) => deletedEntity,
  )

  /**
   *  Service center entity name, derived from the schema. Defaults to 'Service
   *  Center' if the entity is not found in the schema.
   */
  public readonly svcCenterEntityNameSg = ngCore.computed(
    (schema = this._schemaFeature.selectors.schema.asSignal()) =>
      schema.tryGetEntity(SVC_CENTER_ENTITY_ID)?.name ?? 'Service Center',
  )

  /**
   * Table name for service centers, currently derived by pluralizing the entity
   * name.
   */
  public readonly tableNameSg = ngCore.computed(
    // TODO: Handle pluralization properly
    () => this.svcCenterEntityNameSg() + 's',
  )

  /** Handles the confirmation action for service center deletion. */
  public confirmDeletionClickHandler(): void {
    this.serviceCentersEc.plugins.editingPlugin.feature.actions.submit
      .from('ServiceCenters.Delete.Confirmation.Confirm.Clicked')
      .dispatch({ delete: true })
  }

  /** Handles the cancellation action for service center deletion. */
  public cancelDeletionClickHandler(): void {
    this.serviceCentersEc.plugins.editingPlugin.feature.actions.discard
      .from('ServiceCenters.Delete.Confirmation.Cancel.Clicked')
      .dispatch({ delete: true })
  }
}
